import React, { useState, useEffect } from 'react';
import { AppIcon } from '../../icons/appIcons.tsx';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  membershipStatus: string;
}

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  yearsActive: string;
}

interface SendNotificationModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function SendNotificationModal({ onClose, onSuccess }: SendNotificationModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal',
    targetAudience: 'all',
    specificFamilyIds: [] as string[],
    specificAlumniIds: [] as string[],
    expiresAt: ''
  });
  const [families, setFamilies] = useState<Family[]>([]);
  const [alumni, setAlumni] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    fetchFamilies();
    fetchAlumni();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAlumni(data.data);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFamilySelection = (familyId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specificFamilyIds: checked
        ? [...prev.specificFamilyIds, familyId]
        : prev.specificFamilyIds.filter(id => id !== familyId)
    }));
  };

  const handleAlumniSelection = (alumniId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specificAlumniIds: checked
        ? [...prev.specificAlumniIds, alumniId]
        : prev.specificAlumniIds.filter(id => id !== alumniId)
    }));
  };

  const handleSelectAllFamilies = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specificFamilyIds: checked ? families.map(f => f.id) : []
    }));
  };

  const handleSelectAllAlumni = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specificAlumniIds: checked ? alumni.map(a => a.id) : []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    // Validate based on target audience
    if (formData.targetAudience === 'specific_families' && formData.specificFamilyIds.length === 0) {
      setErrors(['Please select at least one family to send the notification to.']);
      setLoading(false);
      return;
    }

    if (formData.targetAudience === 'specific_alumni' && formData.specificAlumniIds.length === 0) {
      setErrors(['Please select at least one alumni member to send the notification to.']);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/notifications', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          specificFamilyIds: formData.targetAudience === 'specific_families' ? formData.specificFamilyIds : undefined,
          specificAlumniIds: formData.targetAudience === 'specific_alumni' ? formData.specificAlumniIds : undefined,
          expiresAt: formData.expiresAt || null
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setErrors(['Failed to send notification. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'urgent': return 'text-red-600';
      case 'payment': return 'text-yellow-600';
      case 'event': return 'text-blue-600';
      case 'maintenance': return 'text-gray-600';
      default: return 'text-green-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'normal': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Send Notification</h3>
                <p className="text-sm text-gray-600">Send an announcement to families, alumni, or both</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <AppIcon name="close" className="w-5 h-5" />
              </button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-red-800 font-medium mb-2">Please fix the following errors:</h4>
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Message Content */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Message Content</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Enter notification title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Enter your message..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                        <option value="general">General</option>
                        <option value="grounds">Ranch Grounds</option>
                        <option value="horse">Horse & Care</option>
                        <option value="payment">Payment</option>
                        <option value="event">Event</option>
                        <option value="urgent">Urgent</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expires At (Optional)</label>
                    <input
                      type="datetime-local"
                      value={formData.expiresAt}
                      onChange={(e) => handleInputChange('expiresAt', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for permanent notification</p>
                  </div>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Recipients</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="all">All (Families & Alumni)</option>
                      <option value="active_families">Active Families Only</option>
                      <option value="all_alumni">All Alumni</option>
                      <option value="specific_families">Select Specific Families</option>
                      <option value="specific_alumni">Select Specific Alumni</option>
                      <option value="families_and_alumni">Families and Alumni</option>
                    </select>
                  </div>

                  {/* Family Selection */}
                  {formData.targetAudience === 'specific_families' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Families ({formData.specificFamilyIds.length} selected)
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto">
                        {families.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green mx-auto mb-2"></div>
                            Loading families...
                          </div>
                        ) : (
                          <>
                            {/* Select All checkbox */}
                            <label className="flex items-center p-2 bg-gray-100 hover:bg-gray-200 rounded mb-2 border-b border-gray-300">
                              <input
                                type="checkbox"
                                checked={formData.specificFamilyIds.length === families.length}
                                onChange={(e) => handleSelectAllFamilies(e.target.checked)}
                                className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-900">
                                Select All Families
                              </span>
                            </label>
                            
                            {/* Individual family checkboxes */}
                            {families.map(family => (
                              <label key={family.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                                <input
                                  type="checkbox"
                                  checked={formData.specificFamilyIds.includes(family.id)}
                                  onChange={(e) => handleFamilySelection(family.id, e.target.checked)}
                                  className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm">
                                  {family.familyName} 
                                  <span className="text-gray-500">
                                    ({family.primaryContactFirstName} {family.primaryContactLastName})
                                  </span>
                                </span>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                      {formData.specificFamilyIds.length === 0 && (
                        <p className="text-sm text-orange-600 mt-2">
                          Please select at least one family to send the notification to.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Alumni Selection */}
                  {formData.targetAudience === 'specific_alumni' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Alumni ({formData.specificAlumniIds.length} selected)
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto">
                        {alumni.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green mx-auto mb-2"></div>
                            Loading alumni...
                          </div>
                        ) : (
                          <>
                            {/* Select All checkbox */}
                            <label className="flex items-center p-2 bg-gray-100 hover:bg-gray-200 rounded mb-2 border-b border-gray-300">
                              <input
                                type="checkbox"
                                checked={formData.specificAlumniIds.length === alumni.length}
                                onChange={(e) => handleSelectAllAlumni(e.target.checked)}
                                className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-900">
                                Select All Alumni
                              </span>
                            </label>
                            
                            {/* Individual alumni checkboxes */}
                            {alumni.map(alumniMember => (
                              <label key={alumniMember.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                                <input
                                  type="checkbox"
                                  checked={formData.specificAlumniIds.includes(alumniMember.id)}
                                  onChange={(e) => handleAlumniSelection(alumniMember.id, e.target.checked)}
                                  className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm">
                                  {alumniMember.firstName} {alumniMember.lastName}
                                  <span className="text-gray-500">
                                    {alumniMember.yearsActive && ` (${alumniMember.yearsActive})`}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                      {formData.specificAlumniIds.length === 0 && (
                        <p className="text-sm text-orange-600 mt-2">
                          Please select at least one alumni member to send the notification to.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Preview */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Preview</h5>
                    <div className={`text-sm ${getTypeColor(formData.type)}`}>
                      {formData.type.charAt(0).toUpperCase() + formData.type.slice(1)} • 
                      <span className={getPriorityColor(formData.priority)}> {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)} Priority</span>
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      {formData.targetAudience === 'all' && 'Recipients: All Families & Alumni'}
                      {formData.targetAudience === 'active_families' && 'Recipients: Active Families'}
                      {formData.targetAudience === 'all_alumni' && 'Recipients: All Alumni'}
                      {formData.targetAudience === 'families_and_alumni' && 'Recipients: All Families & Alumni'}
                      {formData.targetAudience === 'specific_families' && `Recipients: ${formData.specificFamilyIds.length} ${formData.specificFamilyIds.length === 1 ? 'family' : 'families'} selected`}
                      {formData.targetAudience === 'specific_alumni' && `Recipients: ${formData.specificAlumniIds.length} alumni ${formData.specificAlumniIds.length === 1 ? 'member' : 'members'} selected`}
                    </div>
                    <h6 className="font-medium mt-2">{formData.title || 'Enter a title...'}</h6>
                    <p className="text-sm text-gray-600 mt-1">{formData.message || 'Enter your message...'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  loading || 
                  !formData.title || 
                  !formData.message || 
                  (formData.targetAudience === 'specific_families' && formData.specificFamilyIds.length === 0) ||
                  (formData.targetAudience === 'specific_alumni' && formData.specificAlumniIds.length === 0)
                }
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 